import React from 'react';
import {
  IonBackButton,
  IonButtons,
  IonButton,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTitle,
  IonToolbar,
  IonToggle,
  useIonToast,
} from '@ionic/react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { CreateRawMaterialDto, Unit } from 'shared';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';

interface FormValues {
  name: string;
  unit: Unit;
  currentStock: number;
  minStockLevel: number;
  costPerUnit: number;
  isIngredient: boolean;
}

const MaterialForm: React.FC = () => {
  const { t } = useLanguage();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [presentToast] = useIonToast();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      unit: Unit.GRAM,
      currentStock: 0,
      minStockLevel: 0,
      costPerUnit: 0,
      isIngredient: true,
    },
  });

  const createMaterialMutation = useMutation({
    mutationFn: async (payload: CreateRawMaterialDto) => {
      await apiClient.post('/materials', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] });
      queryClient.invalidateQueries({ queryKey: ['materials', 'list'] });
      presentToast({
        message: t('materialCreatedSuccessfully') ?? 'Material created successfully',
        duration: 2000,
        color: 'success',
      });
      history.replace('/tabs/materials');
    },
    onError: () => {
      presentToast({
        message: t('materialCreateFailed') ?? 'Failed to create material',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const onSubmit = async (values: FormValues) => {
    const payload: CreateRawMaterialDto = {
      name: values.name.trim(),
      unit: values.unit,
      currentStock: Number(values.currentStock || 0),
      minStockLevel: Number(values.minStockLevel || 0),
      costPerUnit: Number(values.costPerUnit || 0),
      isIngredient: values.isIngredient,
    };

    await createMaterialMutation.mutateAsync(payload);
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/materials" />
          </IonButtons>
          <IonTitle>{t('addMaterial') ?? 'Add material'}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <form onSubmit={handleSubmit(onSubmit)}>
          <IonList>
            <IonItem>
              <IonLabel position="stacked">{t('name')}</IonLabel>
              <Controller
                control={control}
                name="name"
                rules={{ required: true }}
                render={({ field }) => (
                  <IonInput
                    value={field.value}
                    onIonChange={(event) => field.onChange(event.detail.value ?? '')}
                  />
                )}
              />
              {errors.name ? (
                <IonText color="danger">
                  <p>{t('enterName') ?? 'Enter name'}</p>
                </IonText>
              ) : null}
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">{t('unit')}</IonLabel>
              <Controller
                control={control}
                name="unit"
                rules={{ required: true }}
                render={({ field }) => (
                  <IonSelect
                    value={field.value}
                    onIonChange={(event) => field.onChange(event.detail.value as Unit)}
                  >
                    <IonSelectOption value={Unit.GRAM}>g</IonSelectOption>
                    <IonSelectOption value={Unit.KILOGRAM}>kg</IonSelectOption>
                    <IonSelectOption value={Unit.MILLILITER}>ml</IonSelectOption>
                    <IonSelectOption value={Unit.LITER}>l</IonSelectOption>
                    <IonSelectOption value={Unit.BOTTLE}>{t('bottle') ?? 'Bottle'}</IonSelectOption>
                    <IonSelectOption value={Unit.CAN}>{t('can') ?? 'Can'}</IonSelectOption>
                    <IonSelectOption value={Unit.PACKED}>{t('packed') ?? 'Packed'}</IonSelectOption>
                  </IonSelect>
                )}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">{t('currentStock')}</IonLabel>
              <Controller
                control={control}
                name="currentStock"
                rules={{ min: 0 }}
                render={({ field }) => (
                  <IonInput
                    type="number"
                    value={field.value?.toString() ?? ''}
                    onIonChange={(event) => field.onChange(Number(event.detail.value ?? 0))}
                  />
                )}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">{t('minStockLevel')}</IonLabel>
              <Controller
                control={control}
                name="minStockLevel"
                rules={{ min: 0 }}
                render={({ field }) => (
                  <IonInput
                    type="number"
                    value={field.value?.toString() ?? ''}
                    onIonChange={(event) => field.onChange(Number(event.detail.value ?? 0))}
                  />
                )}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">{t('costPerUnit')}</IonLabel>
              <Controller
                control={control}
                name="costPerUnit"
                rules={{ min: 0 }}
                render={({ field }) => (
                  <IonInput
                    type="number"
                    value={field.value?.toString() ?? ''}
                    onIonChange={(event) => field.onChange(Number(event.detail.value ?? 0))}
                  />
                )}
              />
            </IonItem>

            <IonItem>
              <IonLabel>{t('useAsIngredient') ?? 'Use as ingredient'}</IonLabel>
              <Controller
                control={control}
                name="isIngredient"
                render={({ field }) => (
                  <IonToggle
                    checked={field.value}
                    onIonChange={(event) => field.onChange(event.detail.checked)}
                  />
                )}
              />
            </IonItem>
          </IonList>

          <IonButton
            expand="block"
            type="submit"
            style={{ marginTop: 24 }}
            disabled={isSubmitting || isSubmitSuccessful || createMaterialMutation.isPending}
          >
            {isSubmitting || createMaterialMutation.isPending ? t('saving') ?? 'Saving…' : t('save')}
          </IonButton>
        </form>
      </IonContent>
    </IonPage>
  );
};

export default MaterialForm;
